<template>
    <el-dialog class="dbman-dialog dbman-dialog--favlist" :title="`收藏结果（${list.length}）`"
        :model-value="modelValue" width="min(900px, 94vw)" append-to-body
        @update:model-value="$emit('update:modelValue', $event)">
        <div class="favx">
            <div class="favx__toolbar">
                <span class="favx__block">{{ blockName || 'SQL 块' }}</span>
                <span class="favx__count">{{ list.length }} 组</span>
                <el-button v-if="list.length" size="small" text type="primary" @click="toggleExpandAll">
                    {{ allExpanded ? '全部收起' : '全部展开' }}
                </el-button>
            </div>
            <div v-loading="loading" class="favx__list">
                <el-empty v-if="!loading && list.length === 0" description="暂无结果收藏" :image-size="70" />
                <div v-for="fav in list" :key="fav.id" class="favx-card" :class="{ 'is-open': isOpen(fav) }">
                    <div class="favx-card__head" @click="toggleExpand(fav)">
                        <div class="favx-card__time">
                            <span class="favx-card__date">{{ fav.date }}</span>
                            <span class="favx-card__clock">{{ fav.time }}</span>
                        </div>
                        <div class="favx-card__main">
                            <div class="favx-card__name">
                                <span class="favx-card__title" :title="fav.favoriteName">{{ fav.favoriteName }}</span>
                                <el-tag v-if="fav.isShare" size="small" type="success" effect="light">共享中</el-tag>
                                <span class="favx-card__rows">{{ fav.rowCount }} 行</span>
                                <span v-if="!fav.ownedByMe" class="favx-card__other">他人</span>
                            </div>
                            <div class="favx-card__desc" :title="fav.description">
                                {{ fav.description || '无说明' }}
                            </div>
                        </div>
                        <div class="favx-card__ops">
                            <el-button size="small" plain :type="fav.isShare ? 'success' : 'default'"
                                :disabled="!fav.ownedByMe" @click.stop="toggleFavShare(fav)">
                                {{ fav.isShare ? '取消共享' : '共享' }}
                            </el-button>
                            <el-button size="small" plain type="danger" :disabled="!fav.ownedByMe"
                                @click.stop="confirmDeleteFav(fav)">删除</el-button>
                            <el-icon class="favx-card__arrow" :class="{ 'is-open': isOpen(fav) }">
                                <ArrowRight />
                            </el-icon>
                        </div>
                    </div>
                    <el-collapse-transition>
                        <div v-show="isOpen(fav)" class="favx-card__body">
                            <div v-if="fav.sqlText" class="favx-card__sql">
                                <span class="favx-card__sql-label">执行语句</span>
                                <pre class="favx-card__sql-text">{{ fav.sqlText }}</pre>
                            </div>
                            <vxe-table v-if="isOpen(fav)" :data="fav._rows" :max-height="320" size="small" border
                                stripe show-overflow :row-config="{ keyField: '__idx', isHover: true }"
                                :scroll-y="{ enabled: true, gt: 20 }"
                                :sort-config="{ trigger: 'cell', orders: ['asc', 'desc', null] }">
                                <vxe-column type="seq" width="56" title="#" />
                                <vxe-column v-for="(col, ci) in fav.columns" :key="ci" :field="'c' + ci"
                                    :title="col.name" min-width="140" :sortable="true">
                                    <template #default="{ row }">
                                        <span v-if="row['c' + ci] === null" class="dbconsole-null">NULL</span>
                                        <span v-else>{{ row['c' + ci] }}</span>
                                    </template>
                                </vxe-column>
                            </vxe-table>
                        </div>
                    </el-collapse-transition>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { markRaw } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../../../utils/request.js';

export default {
    name: 'DbFavoriteListDialog',
    props: {
        modelValue: { type: Boolean, default: false },
        sqlBlockId: { type: String, default: '' },
        blockName: { type: String, default: '' }
    },
    emits: ['update:modelValue', 'deleted'],
    data() {
        return {
            loading: false,
            list: [],
            active: []
        };
    },
    computed: {
        allExpanded() {
            return this.list.length > 0 && this.list.every(f => this.active.indexOf(f.id) > -1);
        }
    },
    watch: {
        modelValue(open) {
            if (open) this.load();
        },
        sqlBlockId() {
            if (this.modelValue) this.load();
        }
    },
    methods: {
        load() {
            if (!this.sqlBlockId) {
                this.list = [];
                this.active = [];
                return Promise.resolve();
            }
            this.list = [];
            this.active = [];
            this.loading = true;
            return request({
                url: '/dbManRowFavorite/list',
                method: 'post',
                data: { sqlBlockId: this.sqlBlockId, current: 1, size: 100 }
            }).then(res => {
                if (res.code == 0) {
                    this.list = (res.data || []).map(fav => {
                        const raw = fav.createTime ? String(fav.createTime).replace('T', ' ') : '';
                        const seg = raw.split(' ');
                        return {
                            id: fav.id,
                            favoriteName: fav.favoriteName,
                            description: fav.description,
                            sqlText: fav.sqlText || '',
                            isShare: !!fav.isShare,
                            rowCount: fav.rowCount,
                            date: seg[0] || '',
                            time: (seg[1] || '').slice(0, 8),
                            ownedByMe: !!fav.ownedByMe,
                            columns: markRaw(fav.columns || []),
                            _rows: markRaw((fav.rows || []).map((row, ri) => {
                                const obj = { __idx: ri };
                                row.forEach((cell, ci) => {
                                    obj['c' + ci] = cell;
                                });
                                return obj;
                            }))
                        };
                    });
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        isOpen(fav) {
            return this.active.indexOf(fav.id) > -1;
        },
        toggleExpand(fav) {
            const i = this.active.indexOf(fav.id);
            if (i > -1) this.active.splice(i, 1);
            else this.active.push(fav.id);
        },
        toggleExpandAll() {
            this.active = this.allExpanded ? [] : this.list.map(f => f.id);
        },
        toggleFavShare(fav) {
            const next = !fav.isShare;
            request({
                url: '/dbManRowFavorite/share',
                method: 'post',
                data: { id: fav.id, isShare: next }
            }).then(res => {
                if (res.code == 0) {
                    fav.isShare = next;
                    ElMessage({ message: next ? '已共享' : '已取消共享', type: 'success', duration: 1500 });
                } else {
                    ElMessage({ message: res.msg || '操作失败', type: 'error', duration: 3000 });
                }
            });
        },
        confirmDeleteFav(fav) {
            ElMessageBox.confirm(`删除后不可恢复，确定删除收藏「${fav.favoriteName}」吗？`, '删除确认', {
                confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning'
            }).then(() => {
                request({
                    url: '/dbManRowFavorite/delete',
                    method: 'post',
                    data: { id: fav.id }
                }).then(res => {
                    if (res.code == 0) {
                        ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                        const idx = this.list.findIndex(f => f.id === fav.id);
                        if (idx > -1) this.list.splice(idx, 1);
                        this.active = this.active.filter(id => id !== fav.id);
                        this.$emit('deleted', fav);
                    }
                });
            }).catch(() => { });
        }
    }
};
</script>

<style>
@import './dialog.css';
</style>
